import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Input, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import {
  useGetCharacterizationsQuery,
  type CharacterizationFilters,
  type CharacterizationRow,
} from 'services/exploreService';
import { Empty, PageHead, fmt } from 'components/explore';
import { NameFilter, StudyCountFilter } from './ColumnFilters';

const PAGE_SIZE = 50;
const readable = (value: string | null) => value?.replace(/_/g, ' ') || '–';

/** Keep the UUID in the drill-down: missing severity must not mean "any severity". */
export const characterizationHref = (row: CharacterizationRow) =>
  `/characterizations/${encodeURIComponent(row.uuid)}`;

export const characterizationLabel = (row: CharacterizationRow) =>
  row.value ||
  [row.region, row.condition, row.stratification]
    .filter(Boolean)
    .map(value => value!.replace(/_/g, ' '))
    .join(' · ') ||
  row.icid ||
  row.uuid;

const Characterizations: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = (params.get('q') || '').trim();
  const rawPage = Number(params.get('page') || 1);
  const page = Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const getCount = (name: string) => {
    const raw = params.get(name);
    const value = raw === null || raw === '' ? undefined : Number(raw);
    return value !== undefined && Number.isSafeInteger(value) && value >= 0 ? value : undefined;
  };
  const sortKeys = ['value', 'region', 'condition', 'stratification', 'studies'];
  const sort = (
    sortKeys.includes(params.get('sort') || '') ? params.get('sort') : 'region'
  ) as CharacterizationFilters['sort'];
  const order = params.get('order') === 'desc' ? 'desc' : 'asc';
  const filters: CharacterizationFilters = {
    q: q || undefined,
    name: params.get('name') || undefined,
    region: params.get('region') || undefined,
    condition: params.get('condition') || undefined,
    stratification: params.get('stratification') || undefined,
    min_studies: getCount('min_studies'),
    max_studies: getCount('max_studies'),
    sort,
    order,
    offset: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  };
  const [draft, setDraft] = useState(q);
  useEffect(() => setDraft(q), [q]);
  const { currentData: data, data: cachedData, isFetching, error, refetch } = useGetCharacterizationsQuery(filters);

  const update = (patch: Partial<CharacterizationFilters>, nextPage = 1) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      next.delete(key);
      if (value !== undefined && value !== '') next.set(key, String(value));
    });
    next.delete('page');
    if (nextPage > 1) next.set('page', String(nextPage));
    setParams(next);
  };
  const sortColumn = (key: NonNullable<CharacterizationFilters['sort']>) => ({
    sorter: true,
    sortOrder:
      sort === key ? (order === 'desc' ? ('descend' as const) : ('ascend' as const)) : null,
  });
  const facetColumn = (key: 'region' | 'condition' | 'stratification') => ({
    ...sortColumn(key),
    filters: [
      ...((data?.facets || cachedData?.facets)?.[key] || []).map(value => ({ text: readable(value), value })),
      { text: 'Unspecified', value: '__missing__' },
    ],
    filterMultiple: false,
    filterSearch: true,
    filteredValue: filters[key] ? [filters[key]!] : null,
  });

  const columns: ColumnsType<CharacterizationRow> = [
    {
      title: 'Characterization',
      key: 'value',
      ...sortColumn('value'),
      filteredValue: filters.name ? [filters.name] : null,
      filterDropdown: ({ close }) => (
        <NameFilter value={filters.name} apply={name => update({ name })} close={close} />
      ),
      render: (_, row) => (
        <Link className="ax-link" to={characterizationHref(row)}>
          {characterizationLabel(row)}
        </Link>
      ),
    },
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region',
      render: readable,
      ...facetColumn('region'),
    },
    {
      title: 'Condition',
      dataIndex: 'condition',
      key: 'condition',
      render: readable,
      ...facetColumn('condition'),
    },
    {
      title: 'Severity',
      dataIndex: 'stratification',
      key: 'stratification',
      render: readable,
      ...facetColumn('stratification'),
    },
    {
      title: 'Studies',
      dataIndex: 'studies',
      key: 'studies',
      align: 'right',
      render: fmt,
      ...sortColumn('studies'),
      filteredValue:
        filters.min_studies !== undefined || filters.max_studies !== undefined ? ['range'] : null,
      filterDropdown: ({ close }) => (
        <StudyCountFilter
          min={filters.min_studies}
          max={filters.max_studies}
          apply={(min_studies, max_studies) => update({ min_studies, max_studies })}
          close={close}
        />
      ),
    },
  ];

  return (
    <div className="ax-page">
      <PageHead
        title="Characterization Search"
        lede="Browse all characterizations in the data warehouse, including those without reports. Select one to see its linked studies and reports."
      />
      <Input.Search
        size="large"
        style={{ maxWidth: 560 }}
        prefix={<SearchOutlined />}
        placeholder="e.g. severe aortic stenosis"
        aria-label="Search characterizations"
        maxLength={200}
        value={draft}
        allowClear
        enterButton="Search"
        loading={isFetching}
        onChange={e => {
          setDraft(e.target.value);
          if (!e.target.value) update({ q: undefined });
        }}
        onSearch={value => update({ q: value.trim() || undefined })}
      />
      <p className="ax-faint">
        Every word matches the characterization name, region, condition, or severity—not free-text
        mentions in reports.
      </p>
      {(filters.name ||
        filters.region ||
        filters.condition ||
        filters.stratification ||
        filters.min_studies !== undefined ||
        filters.max_studies !== undefined) && (
        <Button
          type="link"
          onClick={() =>
            update({
              name: undefined,
              region: undefined,
              condition: undefined,
              stratification: undefined,
              min_studies: undefined,
              max_studies: undefined,
            })
          }
        >
          Clear column filters
        </Button>
      )}
      {error ? (
        <Alert
          type="error"
          showIcon
          message="Could not load characterizations"
          description="The characterization catalog could not be loaded. Please try again."
          action={<Button onClick={() => refetch()}>Retry</Button>}
        />
      ) : (
        <div className="ax-panel" style={{ padding: 0 }}>
          <Table<CharacterizationRow>
            className="ax-table"
            size="middle"
            rowKey="uuid"
            columns={columns}
            dataSource={data?.items ?? []}
            loading={isFetching}
            scroll={{ x: 850 }}
            locale={{
              emptyText: (
                <Empty title="No characterizations found">Try fewer or different words.</Empty>
              ),
            }}
            onRow={row => ({
              onClick: e => {
                if (
                  e.defaultPrevented ||
                  e.ctrlKey ||
                  e.metaKey ||
                  e.shiftKey ||
                  e.altKey ||
                  (e.target as HTMLElement).closest('a, button, input, select, textarea')
                )
                  return;
                navigate(characterizationHref(row));
              },
              onKeyDown: e => {
                if (e.key === 'Enter' && e.target === e.currentTarget)
                  navigate(characterizationHref(row));
              },
              tabIndex: 0,
            })}
            onChange={(pagination, selected, sorter, extra) => {
              if (extra.action === 'paginate') {
                update({}, pagination.current || 1);
                return;
              }
              if (extra.action === 'sort') {
                const current = Array.isArray(sorter) ? sorter[0] : sorter;
                update({
                  sort: current.order
                    ? (current.columnKey as CharacterizationFilters['sort'])
                    : undefined,
                  order: current.order ? (current.order === 'descend' ? 'desc' : 'asc') : undefined,
                });
              } else {
                update({
                  region: selected.region?.[0] as string | undefined,
                  condition: selected.condition?.[0] as string | undefined,
                  stratification: selected.stratification?.[0] as string | undefined,
                });
              }
            }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: data?.total ?? 0,
              showSizeChanger: false,
              showTotal: (total, range) => `${fmt(range[0])}–${fmt(range[1])} of ${fmt(total)}`,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Characterizations;
